import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Form,
  InputGroup
} from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { BsChevronLeft } from "react-icons/bs";
import axios from "axios";
import Swal from "sweetalert2";
import AppConstants from "../../AppConstants";
import useAuthToken from "../../hooks/useAuthToken";
import iconSubscription from "../../images/icon-subscribe.png";
import Header from "../../components/Header";
import "./style.scss";

interface PlanData {
  basePrice: number;
  pricePerUser: number;
  currentUsers: number;
  maxUsers: number;
  planName: string;
}

const UpgradePlan: React.FC = () => {
  const navigate = useNavigate();
  const { userToken, ready, getUserIdFromToken } = useAuthToken();

  // State for API data
  const [planData, setPlanData] = useState<PlanData>({
    basePrice: 0.0,
    pricePerUser: 0.0,
    currentUsers: 1,
    maxUsers: 0,
    planName: "Standard Plan"
  });

  const [isLoading, setIsLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState(1);
  const [showWarning, setShowWarning] = useState(false);
  const [isCounterLoading, setIsCounterLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<any>({});
  const [apiPricingData, setApiPricingData] = useState<any>(null);
  const [proratedAmount, setProratedAmount] = useState<number>(0);

  // Function to fetch pricing by licenses (similar to CreatePlan)
  const fetchPricingByLicenses = async (licenses: number) => {
    try {
      const url = `${AppConstants.PRICING_BY_LICENSES_API}?licenses=${licenses}`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json"
      };

      if (userToken) {
        headers.Authorization = `Bearer ${userToken}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setApiPricingData(data);
      return data;
    } catch (error) {
      console.error("Error fetching pricing by licenses:", error);
      throw error;
    }
  };

  // Function to call prorated amount API
  const callProratedAmountAPI = async (newPriceId: string, subId?: string) => {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json"
      };

      if (userToken) {
        headers.Authorization = `Bearer ${userToken}`;
      }

      // Use provided subId or fallback to state subscriptionData.subscriptionId
      const subscriptionIdToUse = subId || subscriptionData.subscriptionId;

      const response = await fetch(
        AppConstants.SUBSCRIPTION_PRORATED_AMOUNT_API,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            subscriptionId: subscriptionIdToUse,
            newPriceId: newPriceId
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store prorated amount from response
      if (data.success && data.data && data.data.proratedAmount !== undefined) {
        setProratedAmount(data.data.proratedAmount);
      }

      return data;
    } catch (error) {
      console.error("Error calling prorated amount API:", error);
      throw error;
    }
  };

  // Fetch license usage data on component load
  useEffect(() => {
    const fetchLicenseUsage = async () => {
      if (!ready) return;

      try {
        const response = await axios.get(AppConstants.LICENSE_USAGE_API, {
          headers: {
            Authorization: `Bearer ${userToken}`
          }
        });

        // Update plan data with API response
        if (response.data.success && response.data.data) {
          const data = response.data.data;

          setPlanData({
            basePrice: data.currentPlanPrice,
            pricePerUser: 0, // This might need to come from another API
            currentUsers: data.activeUsers,
            maxUsers: data.maxUsers,
            planName: data.planName
          });
          setSubscriptionData(data);

          // Fetch initial pricing for current users + 1 (default counter)
          if (data.maxUsers) {
            const totalLicenses = data.maxUsers + 1; // maxUsers + initial counter of 1
            const pricingData = await fetchPricingByLicenses(totalLicenses);

            // Get the price ID from the pricing data and call prorated API
            const newPriceId = pricingData?.pricingConfig?.tier?.stripePriceId;
            if (newPriceId && data.subscriptionId) {
              // Use data.subscriptionId directly instead of state subscriptionData.subscriptionId
              await callProratedAmountAPI(newPriceId, data.subscriptionId);
            }
          }
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching license usage:", error);
        setIsLoading(false);
      }
    };

    fetchLicenseUsage();
  }, [ready, userToken]); // Wait for auth hook to be ready

  const handleUserChange = async (increment: boolean) => {
    setIsCounterLoading(true);

    // Maximum total users allowed is 20
    const maxTotalUsers = 20;
    const maxAdditionalUsers = maxTotalUsers - planData.maxUsers;

    if (increment && selectedUsers < maxAdditionalUsers) {
      const newUserCount = selectedUsers + 1;
      setSelectedUsers(newUserCount);
      setShowWarning(false);

      try {
        // Calculate total licenses: maxUsers + counter
        const totalLicenses = planData.maxUsers + newUserCount;

        // Fetch pricing for the total license count
        const pricingData = await fetchPricingByLicenses(totalLicenses);

        // Get the price ID from the pricing data
        const newPriceId = pricingData?.pricingConfig?.tier?.stripePriceId;

        if (newPriceId && subscriptionData.subscriptionId) {
          // Call prorated amount API
          await callProratedAmountAPI(newPriceId);
        } else {
          console.warn(
            "Missing priceId or subscriptionId for prorated amount API"
          );
        }
      } catch (error) {
        console.error("Error handling user increment:", error);
      }
    } else if (!increment && selectedUsers > 1) {
      const newUserCount = selectedUsers - 1;
      setSelectedUsers(newUserCount);

      // Show warning if reducing below current users
      if (newUserCount < planData.currentUsers) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      try {
        // Calculate total licenses: maxUsers + counter
        const totalLicenses = planData.maxUsers + newUserCount;

        // Fetch pricing for the total license count
        await fetchPricingByLicenses(totalLicenses);
      } catch (error) {
        console.error("Error fetching pricing for decrement:", error);
      }
    }

    setIsCounterLoading(false);
  };

  const formatBillingCycleDate = () => {
    if (!subscriptionData.currentPeriodEnd) return "09/05/2025";

    try {
      const date = new Date(subscriptionData.currentPeriodEnd);
      return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "09/05/2025";
    }
  };

  const handleContinueToPayment = () => {
    // Navigate to PaymentMethods with subscription update data
    const subscriptionUpdateData = {
      customerId: getUserIdFromToken(), // Get from token
      plan: apiPricingData?.pricingConfig?.tier?.name,
      priceId: apiPricingData?.pricingConfig?.tier?.stripePriceId || null, // Use stripePriceId from current pricing API call
      maxUsers: planData.maxUsers + selectedUsers, // Current max users + increase count = new total
      proratedAmount: proratedAmount,
      isSubscriptionUpdate: true // Flag to identify this as an update flow
    };

    console.log(
      "🚀 Navigating to PaymentMethods with subscription update data:",
      subscriptionUpdateData
    );

    navigate("/payment-methods", {
      state: subscriptionUpdateData
    });
  };

  const handleCancel = () => {
    navigate("/plan-details");
  };

  // Show loading state until auth is ready
  if (!ready) {
    return (
      <div className="upgrade-plan-page">
        <Header
          title="Upgrade Plan"
          showMenu={true}
          currentPage="upgrade-plan"
        />
        <Container className="pb-4">
          <div className="text-center py-5">
            <div
              className="spinner-border text-success"
              style={{ color: "#28a745" }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Initializing...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="upgrade-plan-page">
      <Header title="Upgrade Plan" showMenu={true} currentPage="upgrade-plan" />
      <Container className="pb-4">
        <Row className="mb-4 justify-content-md-center">
          <Col xl={10} lg={10} md={12} sm={12}>
            <Row className="align-items-center my-4">
              <Col xs="auto">
                <Button
                  variant="link"
                  className="p-0 text-dark d-flex align-items-center"
                  onClick={() => navigate("/plan-details")}
                  style={{ fontSize: "16px", textDecoration: "none" }}
                >
                  <BsChevronLeft className="me-1" />
                  Back
                </Button>
              </Col>
            </Row>

            {isLoading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-success"
                  style={{ color: "#28a745" }}
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading plan details...</p>
              </div>
            ) : (
              <>
                {/* Plan Card */}
                <Card
                  className="plan-upgrade-card"
                  style={{ borderRadius: "16px", border: "none" }}
                >
                  <Card.Body className="p-4">
                    <Row className="align-items-center mb-4">
                      <Col>
                        <div className="d-flex align-items-center">
                          <div
                            className="plan-icon me-3"
                            style={{
                              width: "50px",
                              height: "50px",
                              backgroundColor: "#FF9D00",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "white",
                              fontSize: "20px"
                            }}
                          >
                            <img
                              src={iconSubscription}
                              alt="Subscription Icon"
                              width={24}
                            />
                          </div>
                          <div>
                            <h5 className="mb-0 text-white">Standard Plan</h5>
                            <p
                              className="mb-0 text-white"
                              style={{ fontSize: "14px", opacity: 0.8 }}
                            >
                              {planData.maxUsers} Users
                            </p>
                          </div>
                        </div>
                      </Col>
                      <Col xs="auto">
                        <div className="text-white text-end">
                          <div style={{ fontSize: "14px", opacity: 0.8 }}>
                            Monthly
                          </div>
                          <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                            ${planData.basePrice.toFixed(2)}
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* User Selection Section */}
                    <Card
                      className="user-selection-card mb-4"
                      style={{
                        backgroundColor: "white",
                        border: "none",
                        borderRadius: "12px"
                      }}
                    >
                      <Card.Body className="p-4">
                        <h5
                          className="text-center mb-2"
                          style={{ color: "#333" }}
                        >
                          How many users would you like to increase?
                        </h5>
                        <p
                          className="text-center text-muted mb-4"
                          style={{ fontSize: "14px" }}
                        >
                          Current: {planData.maxUsers} users • Maximum: 20 users
                          • Available to add: {20 - planData.maxUsers}
                        </p>

                        <div className="d-flex align-items-center justify-content-center mb-4">
                          <Button
                            variant="outline-secondary"
                            className="rounded-circle"
                            style={{
                              width: "50px",
                              height: "50px",
                              fontSize: "20px"
                            }}
                            onClick={() => handleUserChange(false)}
                            disabled={selectedUsers <= 1 || isCounterLoading}
                          >
                            −
                          </Button>

                          <div
                            className="mx-4 px-4 py-2 text-center"
                            style={{
                              fontSize: "32px",
                              fontWeight: "bold",
                              minWidth: "80px",
                              color: "#333"
                            }}
                          >
                            {selectedUsers}
                          </div>

                          <Button
                            variant="outline-secondary"
                            className="rounded-circle"
                            style={{
                              width: "50px",
                              height: "50px",
                              fontSize: "20px"
                            }}
                            onClick={() => handleUserChange(true)}
                            disabled={
                              selectedUsers >= 20 - planData.maxUsers ||
                              isCounterLoading
                            }
                          >
                            +
                          </Button>
                        </div>

                        {/* Price Display */}
                        <div className="text-center mb-3">
                          <div
                            className="price-display d-inline-block px-4 py-2"
                            style={{
                              backgroundColor: "#E8E8E8",
                              borderRadius: "20px",
                              fontSize: "20px",
                              fontWeight: "bold",
                              color: "#333",
                              minHeight: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            {isCounterLoading ? (
                              <div
                                className="spinner-border"
                                role="status"
                                style={{
                                  color: "#28a745",
                                  width: "1.5rem",
                                  height: "1.5rem"
                                }}
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            ) : (
                              `$${proratedAmount.toFixed(2)}`
                            )}
                          </div>
                        </div>

                        <p className="text-center text-muted mb-3">
                          You need to pay ${proratedAmount.toFixed(2)} for{" "}
                          {selectedUsers} users for a duration of 30 days.
                        </p>

                        {selectedUsers > 0 && (
                          <div
                            className="billing-info text-center p-3"
                            style={{
                              backgroundColor: "#E8F5E8",
                              borderRadius: "12px",
                              fontSize: "14px",
                              color: "#333"
                            }}
                          >
                            Starting from the next billing cycle (
                            {formatBillingCycleDate()}), your total will be $
                            {apiPricingData?.pricingConfig?.tier?.unitAmount?.toFixed(
                              2
                            ) || "0.00"}{" "}
                            for {planData.maxUsers + selectedUsers} users.
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Card.Body>
                </Card>

                {/* Action Buttons */}
                <div className="mt-4">
                  <Button
                    className="w-100 py-3 mb-3"
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "white"
                    }}
                    onClick={handleContinueToPayment}
                    disabled={isCounterLoading}
                  >
                    Continue to Payment
                  </Button>

                  <Button
                    variant="link"
                    className="w-100 text-success"
                    onClick={handleCancel}
                    style={{ fontSize: "16px" }}
                  >
                    Cancel
                  </Button>
                </div>

                {/* Back To App Button */}
                <div className="text-center mt-4 mb-3">
                  <Button
                    variant="outline-success"
                    className="px-4 py-2"
                    style={{
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontWeight: "600",
                      borderColor: "#28a745",
                      color: "#28a745",
                      backgroundColor: "transparent"
                    }}
                    onMouseEnter={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = "#28a745";
                      target.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      const target = e.target as HTMLButtonElement;
                      target.style.backgroundColor = "transparent";
                      target.style.color = "#28a745";
                    }}
                    onClick={() => {
                      window.location.replace("pretrip://subscription-success");
                    }}
                  >
                    Back To App
                  </Button>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UpgradePlan;
